import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Camera, Type, Mic, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { patientActivities } from '../services/api';
import ActivityTimeline from './ActivityTimeline';
import ActivityDashboard from './ActivityDashboard';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

interface PatientActivityLogProps {
  patientId: string;
}

const PatientActivityLog: React.FC<PatientActivityLogProps> = ({ patientId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedInputMethod, setSelectedInputMethod] = useState<'photo' | 'text' | 'voice' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    category: 'medication',
    activityType: 'medication_taken',
    description: '',
    medicationName: '',
    dosage: '',
    systolic: '',
    diastolic: '',
    heartRate: '',
    notes: ''
  });

  // OCR state
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState<any>(null);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await patientActivities.getAll(patientId, { limit: 100 });
      if (response.success) {
        setActivities(response.activities || []);
      }
    } catch (error: any) {
      console.error('Error fetching activities:', error);
      setError(t('patientActivity.loadError'));
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await patientActivities.getSummary(patientId, 30);
      if (response.success) {
        setSummary(response.summary);
      }
    } catch (error: any) {
      console.error('Error fetching summary:', error);
    }
  }, [patientId]);

  useEffect(() => {
    fetchActivities();
    fetchSummary();
  }, [fetchActivities, fetchSummary]);

  const handleAddActivity = () => {
    setShowAddModal(true);
    setSelectedInputMethod(null);
    setFormData({
      category: 'medication',
      activityType: 'medication_taken',
      description: '',
      medicationName: '',
      dosage: '',
      systolic: '',
      diastolic: '',
      heartRate: '',
      notes: ''
    });
    setOcrResult(null);
  };

  const handleSelectInputMethod = (method: 'photo' | 'text' | 'voice') => {
    setSelectedInputMethod(method);
    if (method === 'photo') {
      // Trigger file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment' as any;
      input.onchange = (e: any) => {
        const file = e.target?.files?.[0];
        if (file) {
          handlePhotoCapture(file);
        }
      };
      input.click();
    }
  };

  const handlePhotoCapture = async (file: File) => {
    try {
      setOcrProcessing(true);
      setFormData(prev => ({ ...prev, category: 'vital_signs', activityType: 'bp_reading' }));

      const response = await patientActivities.uploadBPPhoto(patientId, file);

      if (response.success && response.ocr_data) {
        setOcrResult(response.ocr_data);
        setFormData(prev => ({
          ...prev,
          systolic: response.ocr_data.systolic?.toString() || '',
          diastolic: response.ocr_data.diastolic?.toString() || '',
          heartRate: response.ocr_data.heart_rate?.toString() || '',
          description: `血压: ${response.ocr_data.systolic || '--'}/${response.ocr_data.diastolic || '--'} mmHg`
        }));
      } else {
        setError(t('patientActivity.addActivityModal.ocrFailed'));
      }
    } catch (error: any) {
      console.error('Error processing photo:', error);
      setError(t('patientActivity.addActivityModal.ocrFailed'));
    } finally {
      setOcrProcessing(false);
    }
  };

  const handleSaveActivity = async () => {
    try {
      setLoading(true);

      const activityData: any = {
        category: formData.category,
        activity_type: formData.activityType,
        description: formData.description,
        notes: formData.notes,
        input_method: selectedInputMethod === 'photo' ? 'photo_ocr' : 'text'
      };

      if (formData.category === 'medication') {
        activityData.medication_name = formData.medicationName;
        activityData.dosage = formData.dosage;
      }

      if (formData.category === 'vital_signs' && formData.activityType === 'bp_reading') {
        activityData.systolic = formData.systolic ? parseInt(formData.systolic) : undefined;
        activityData.diastolic = formData.diastolic ? parseInt(formData.diastolic) : undefined;
        activityData.heart_rate = formData.heartRate ? parseInt(formData.heartRate) : undefined;
      }

      const response = await patientActivities.create(patientId, activityData);

      if (response.success) {
        setShowAddModal(false);
        await fetchActivities();
        await fetchSummary();
      }
    } catch (error: any) {
      console.error('Error saving activity:', error);
      setError('Failed to save activity');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (!window.confirm(t('patientActivity.deleteConfirm'))) {
      return;
    }

    try {
      const success = await patientActivities.delete(patientId, activityId);
      if (success) {
        await fetchActivities();
        await fetchSummary();
      }
    } catch (error: any) {
      console.error('Error deleting activity:', error);
      setError(t('patientActivity.deleteError'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">{t('patientActivity.title')}</h2>
        <Button
          onClick={handleAddActivity}
          className="rounded-2xl shadow-lg bg-primary hover:bg-primary/90 font-bold"
        >
          <Plus className="h-5 w-5 mr-2" />
          {t('patientActivity.addRecord')}
        </Button>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border-l-4 border-red-500 text-red-800 px-6 py-4 rounded-2xl"
          >
            <p>{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      {loading && !activities.length ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2 rounded-2xl bg-gray-100 p-1">
            <TabsTrigger value="timeline" className="rounded-xl font-bold">
              {t('patientActivity.timeline')}
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="rounded-xl font-bold">
              {t('patientActivity.dashboard')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-6">
            <ActivityTimeline activities={activities} onDelete={handleDeleteActivity} />
          </TabsContent>

          <TabsContent value="dashboard" className="mt-6">
            <ActivityDashboard activities={activities} summary={summary} />
          </TabsContent>
        </Tabs>
      )}

      {/* Add Activity Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 space-y-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {t('patientActivity.addActivityModal.title')}
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowAddModal(false)}
                    className="rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                {/* Input Method Selection */}
                {!selectedInputMethod && (
                  <div className="grid grid-cols-3 gap-4">
                    <Button
                      onClick={() => handleSelectInputMethod('photo')}
                      className="h-24 flex-col gap-2 rounded-2xl border-2 border-dashed hover:border-primary hover:bg-primary/5"
                      variant="outline"
                    >
                      <Camera className="h-8 w-8" />
                      <span className="font-bold">{t('patientActivity.inputMethods.photo')}</span>
                    </Button>
                    <Button
                      onClick={() => handleSelectInputMethod('text')}
                      className="h-24 flex-col gap-2 rounded-2xl border-2 border-dashed hover:border-primary hover:bg-primary/5"
                      variant="outline"
                    >
                      <Type className="h-8 w-8" />
                      <span className="font-bold">{t('patientActivity.inputMethods.text')}</span>
                    </Button>
                    <Button
                      onClick={() => handleSelectInputMethod('voice')}
                      className="h-24 flex-col gap-2 rounded-2xl border-2 border-dashed hover:border-primary hover:bg-primary/5 opacity-50"
                      variant="outline"
                      disabled
                    >
                      <Mic className="h-8 w-8" />
                      <span className="font-bold text-xs">{t('patientActivity.inputMethods.voice')}</span>
                    </Button>
                  </div>
                )}

                {/* OCR Processing */}
                {ocrProcessing && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('patientActivity.addActivityModal.ocrProcessing')}</p>
                  </div>
                )}

                {/* Form */}
                {selectedInputMethod && !ocrProcessing && (
                  <div className="space-y-4">
                    {/* Category Selection */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        {t('patientActivity.addActivityModal.category')}
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary"
                        disabled={selectedInputMethod === 'photo'}
                      >
                        <option value="medication">{t('patientActivity.categories.medication')}</option>
                        <option value="vital_signs">{t('patientActivity.categories.vital_signs')}</option>
                      </select>
                    </div>

                    {/* Medication Fields */}
                    {formData.category === 'medication' && (
                      <>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-gray-700">
                            {t('patientActivity.addActivityModal.medication.name')}
                          </label>
                          <Input
                            value={formData.medicationName}
                            onChange={(e) => setFormData({ ...formData, medicationName: e.target.value })}
                            className="rounded-xl"
                            placeholder="例如: 阿司匹林"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-gray-700">
                            {t('patientActivity.addActivityModal.medication.dosage')}
                          </label>
                          <Input
                            value={formData.dosage}
                            onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                            className="rounded-xl"
                            placeholder="例如: 100mg"
                          />
                        </div>
                      </>
                    )}

                    {/* Vital Signs Fields */}
                    {formData.category === 'vital_signs' && (
                      <>
                        {ocrResult && (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            {t('patientActivity.addActivityModal.ocrSuccess')}
                          </Badge>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">
                              {t('patientActivity.addActivityModal.vitalSigns.systolic')}
                            </label>
                            <Input
                              type="number"
                              value={formData.systolic}
                              onChange={(e) => setFormData({ ...formData, systolic: e.target.value })}
                              className="rounded-xl"
                              placeholder="120"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">
                              {t('patientActivity.addActivityModal.vitalSigns.diastolic')}
                            </label>
                            <Input
                              type="number"
                              value={formData.diastolic}
                              onChange={(e) => setFormData({ ...formData, diastolic: e.target.value })}
                              className="rounded-xl"
                              placeholder="80"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-700">
                              {t('patientActivity.addActivityModal.vitalSigns.heartRate')}
                            </label>
                            <Input
                              type="number"
                              value={formData.heartRate}
                              onChange={(e) => setFormData({ ...formData, heartRate: e.target.value })}
                              className="rounded-xl"
                              placeholder="75"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Description */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        {t('patientActivity.addActivityModal.description')}
                      </label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="rounded-xl min-h-[80px]"
                        placeholder={formData.category === 'medication' ? '服用阿司匹林 100mg' : '血压: 120/80 mmHg'}
                      />
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700">
                        {t('patientActivity.addActivityModal.notes')}
                      </label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="rounded-xl min-h-[60px]"
                        placeholder="备注..."
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={() => setShowAddModal(false)}
                        variant="outline"
                        className="flex-1 rounded-xl font-bold"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        onClick={handleSaveActivity}
                        className="flex-1 rounded-xl font-bold bg-primary hover:bg-primary/90"
                      >
                        {t('common.save')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PatientActivityLog;
